import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useStore } from '../../../../hooks/useStore';
import { Outlet, useNavigate, useParams } from 'react-router';
import { Mode } from '../../../../lib/components';
import { useListEmployeesByGroup } from '../../../../queries/employee/group';
import { EmployeeListItem } from '@pxtra-client-lib-legacy/gen';

interface GroupContextValue {
  employeesInGroup: EmployeeListItem[];
  employeesNotInGroup: EmployeeListItem[];
  isLoadingInGroup: boolean;
  isFetchingInGroup: boolean;
  isLoadingNotInGroup: boolean;
  isFetchingNotInGroup: boolean;
  moveToGroup: (id: string) => void;
  moveMultipleToGroup: (ids: string[]) => void;
  removeFromGroup: (id: string) => void;
  isNewlyAdded: (id: string) => boolean;
  isNewlyRemoved: (id: string) => boolean;
  canSubmit: boolean;
  getAddedIds: () => string[];
  getRemovedIds: () => string[];
  clearChanges: () => void;
  selectedGroup?: string;
  setSelectedGroup: Dispatch<SetStateAction<string | undefined>>;
  mode: Mode;
  setMode: Dispatch<SetStateAction<Mode>>;
}

const groupsContext = React.createContext<GroupContextValue | null>(null);

export const GroupContextProvider = ({ children }: React.PropsWithChildren) => {
  const { companyStore } = useStore();
  const params = useParams();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>(params.groupId ? 'edit' : 'view');
  const [selectedGroup, setSelectedGroup] = useState<string | undefined>(
    params.groupId ?? companyStore.groups?.at(0)?.id
  );

  const [changes, setChanges] = useState<{ added: Set<string>; removed: Set<string> }>({
    added: new Set(),
    removed: new Set()
  });

  const companyId = companyStore.company?.id;

  const {
    data: inGroupData,
    isLoading: isLoadingInGroup,
    isFetching: isFetchingInGroup
  } = useListEmployeesByGroup(companyId, selectedGroup, false);

  const {
    data: notInGroupData,
    isLoading: isLoadingNotInGroup,
    isFetching: isFetchingNotInGroup
  } = useListEmployeesByGroup(companyId, selectedGroup, true);

  const serverInGroup = useMemo(
    () => inGroupData?.pages?.flatMap((page) => page) ?? [],
    [inGroupData]
  );

  const serverNotInGroup = useMemo(
    () => notInGroupData?.pages?.flatMap((page) => page) ?? [],
    [notInGroupData]
  );

  const employeesInGroup = useMemo(() => {
    const removedSet = changes.removed;
    const addedSet = changes.added;

    const filtered = serverInGroup.filter((e) => !removedSet.has(e.id ?? ''));
    const addedEmployees = serverNotInGroup.filter((e) => addedSet.has(e.id ?? ''));

    return [...addedEmployees, ...filtered];
  }, [serverInGroup, serverNotInGroup, changes]);

  const employeesNotInGroup = useMemo(() => {
    const addedSet = changes.added;
    const removedSet = changes.removed;

    const filtered = serverNotInGroup.filter((e) => !addedSet.has(e.id ?? ''));
    const removedEmployees = serverInGroup.filter((e) => removedSet.has(e.id ?? ''));

    return [...removedEmployees, ...filtered];
  }, [serverNotInGroup, serverInGroup, changes]);

  const moveToGroup = useCallback((id: string) => {
    setChanges((prev) => {
      const added = new Set(prev.added);
      const removed = new Set(prev.removed);

      if (removed.has(id)) {
        removed.delete(id);
      } else {
        added.add(id);
      }

      return { added, removed };
    });
  }, []);

  const moveMultipleToGroup = useCallback((ids: string[]) => {
    setChanges((prev) => {
      const added = new Set(prev.added);
      const removed = new Set(prev.removed);

      for (const id of ids) {
        if (removed.has(id)) {
          removed.delete(id);
        } else {
          added.add(id);
        }
      }

      return { added, removed };
    });
  }, []);

  const removeFromGroup = useCallback((id: string) => {
    setChanges((prev) => {
      const added = new Set(prev.added);
      const removed = new Set(prev.removed);

      if (added.has(id)) {
        added.delete(id);
      } else {
        removed.add(id);
      }

      return { added, removed };
    });
  }, []);

  const isNewlyAdded = useCallback((id: string) => changes.added.has(id), [changes]);

  const isNewlyRemoved = useCallback((id: string) => changes.removed.has(id), [changes]);

  const canSubmit = changes.added.size + changes.removed.size > 0;

  const getAddedIds = useCallback(() => Array.from(changes.added), [changes]);
  const getRemovedIds = useCallback(() => Array.from(changes.removed), [changes]);

  const clearChanges = useCallback(() => {
    setChanges({ added: new Set(), removed: new Set() });
  }, []);

  useEffect(() => {
    if (mode === 'edit') {
      navigate(selectedGroup ?? '');
    } else {
      navigate('');
    }
  }, [mode]);

  const contextValue = useMemo<GroupContextValue>(
    () => ({
      employeesInGroup,
      employeesNotInGroup,
      isLoadingInGroup,
      isFetchingInGroup,
      isLoadingNotInGroup,
      isFetchingNotInGroup,
      moveToGroup,
      moveMultipleToGroup,
      removeFromGroup,
      isNewlyAdded,
      isNewlyRemoved,
      canSubmit,
      getAddedIds,
      getRemovedIds,
      clearChanges,
      selectedGroup,
      setSelectedGroup,
      mode,
      setMode
    }),
    [
      employeesInGroup,
      employeesNotInGroup,
      isLoadingInGroup,
      isFetchingInGroup,
      isLoadingNotInGroup,
      isFetchingNotInGroup,
      moveToGroup,
      moveMultipleToGroup,
      removeFromGroup,
      isNewlyAdded,
      isNewlyRemoved,
      canSubmit,
      getAddedIds,
      getRemovedIds,
      clearChanges,
      selectedGroup,
      mode
    ]
  );

  return (
    <groupsContext.Provider value={contextValue}>
      <Outlet />
      {children}
    </groupsContext.Provider>
  );
};

export const useGroupContext = () => {
  const groupContext = useContext(groupsContext);
  if (!groupContext) {
    throw new Error('No store');
  }

  return groupContext;
};
